import {t} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {DateCell} from '../../../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../../../components/common/table/cells/emptyCell';
import {NameCell} from '../../../../components/common/table/cells/nameCell';
import {TeammateCellContainer} from '../../../../components/common/table/cells/teammateCellContainer';
import {TextCell} from '../../../../components/common/table/cells/textCell';
import {TableColumn} from '../../../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../../../components/common/table/tableRow';
import {
  LocalApplicationModel,
  LocalApplicationStatusesEnum,
} from '../../../../models/api/localApplicationModel';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {DevelopersApplicationTableColumnsEnum} from './developersApplicationsTableColumns';

/*
 * Props.
 */

interface DevelopersApplicationsTableRowProps {
  tableColumns: ReadonlyArray<TableColumn<DevelopersApplicationTableColumnsEnum>>;
  localApplication?: LocalApplicationModel;
  options: TableRowRenderOptions;

  onSelectLocalApplication: ClickEventHandler;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const DevelopersApplicationsTableRow: FC<DevelopersApplicationsTableRowProps> = (props) => {
  const {localApplication, options, tableColumns, onSelectLocalApplication} = props;
  const localize = useLocalize();

  if (!localApplication) {
    return null;
  }

  const cellRenderer = (
    index: number,
    column: TableColumn<DevelopersApplicationTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case DevelopersApplicationTableColumnsEnum.NAME:
        return (
          <NameCell
            key={DevelopersApplicationTableColumnsEnum.NAME}
            value={localApplication.name}
            iconName="integrationsFilledSmall"
            description={localApplication.description}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersApplicationTableColumnsEnum.STATUS:
        return (
          <TextCell
            key={DevelopersApplicationTableColumnsEnum.STATUS}
            value={renderStatus(localApplication.status, localize)}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersApplicationTableColumnsEnum.CREATOR:
        return (
          <TeammateCellContainer
            key={DevelopersApplicationTableColumnsEnum.CREATOR}
            teammateId={localApplication.creatorId}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      case DevelopersApplicationTableColumnsEnum.CREATED_DATE:
        return (
          <DateCell
            key={DevelopersApplicationTableColumnsEnum.CREATED_DATE}
            value={localApplication.createdAt}
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
            shouldShowStickyGradient={shouldShowStickyGradient}
          />
        );
      default: {
        return (
          <EmptyCell settings={column.settings} isHovering={isHovering} size={options.size} index={index} />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        onSelectLocalApplication(event);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
    />
  );
};

/*
 * Helpers.
 */

function renderStatus(status: LocalApplicationStatusesEnum, localize: Localizer) {
  switch (status) {
    case LocalApplicationStatusesEnum.PUBLIC:
      return localize(t('settings-developers-applications-table-status-public')`Public`);
    case LocalApplicationStatusesEnum.PRIVATE:
    default:
      return localize(t('settings-developers-applications-table-status-private')`Private`);
  }
}
